import React, { useState, useEffect } from 'react';
import { useParams, useLocation } from 'react-router-dom'; 
import { 
  Container, Paper, Typography, Box, TextField, 
  Button, Radio, RadioGroup, FormControlLabel, 
  CircularProgress, Alert, Stepper, Snackbar, Step, StepLabel, Chip, Checkbox, FormGroup
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import PersonIcon from '@mui/icons-material/Person';
import DeleteIcon from '@mui/icons-material/Delete';
import { ENDPOINTS } from '../config';

const PublicSurveyPage = () => {
  const { token } = useParams(); 
  const location = useLocation(); // Hook to parse search query string

  // Parse Handshake attributes from the URL (passed from the configurator redirect)
  const queryParams = new URLSearchParams(location.search);
  const configurationId = queryParams.get('quoteId') || queryParams.get('configurationId') || '';
  const initialName = queryParams.get('name') || '';
  const initialEmail = queryParams.get('email') || '';
  const initialPhone = queryParams.get('phone') || '';

  const [survey, setSurvey] = useState(null);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [completed, setCompleted] = useState(false);
  const [error, setError] = useState('');

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  
  const [activeStep, setActiveStep] = useState(0);
  const [customerInfo, setCustomerInfo] = useState({
    name: initialName,   // Pre-fill from URL Handshake
    email: initialEmail, // Pre-fill from URL Handshake
    phone: initialPhone  // Pre-fill from URL Handshake
  });

  useEffect(() => {
    const fetchSurvey = async () => {
      try {
        const DEFAULT_UUID = '00000000-0000-0000-0000-000000000000';
        
        // If no token or if it's explicitly 'global', resolve template fallback
        const targetUrl = (!token || token === 'global' || token === DEFAULT_UUID) 
          ? `${ENDPOINTS.SURVEYS}/resolve` 
          : `${ENDPOINTS.SURVEYS}/public/${token}`;

        const res = await fetch(targetUrl);
        if (!res.ok) throw new Error("Survey not found or expired.");
        const data = await res.json();
        setSurvey(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchSurvey();
  }, [token]);

  const handleInputChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleFileChange = (questionId, files) => {
    const fileArray = Array.from(files);
    if (fileArray.length > 5) {
      showSnackbar("Maximum 5 images allowed", "warning");
      return;
    }
    setAnswers(prev => ({ ...prev, [questionId]: fileArray }));
  };

  const removeFile = (questionId, fileIndex) => {
    setAnswers(prev => {
      const currentFiles = prev[questionId] || [];
      const updatedFiles = currentFiles.filter((_, index) => index !== fileIndex);
      return { ...prev, [questionId]: updatedFiles };
    });
  };

  const getQuestionType = (typeValue) => {
    if (typeof typeValue === 'string') return typeValue;
    const typeMap = {
      0: 'Text', 1: 'Paragraph', 2: 'MultipleChoice',
      3: 'Checkbox', 4: 'YesNo', 5: 'ImageUpload'
    };
    return typeMap[typeValue] || 'Text';
  };

  const validateCustomerInfo = () => {
    if (!customerInfo.name.trim()) {
      showSnackbar("Please enter your name", "error");
      return false;
    }
    if (!customerInfo.email.trim()) {
      showSnackbar("Please enter your email", "error");
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerInfo.email)) {
      showSnackbar("Please enter a valid email", "error");
      return false;
    }
    return true;
  };

  const handleNext = () => {
    if (activeStep === 0 && !validateCustomerInfo()) return;
    setActiveStep(prev => prev + 1);
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    const formData = new FormData();

    formData.append('CustomerName', customerInfo.name);
    formData.append('CustomerEmail', customerInfo.email);
    formData.append('CustomerPhone', customerInfo.phone);
    
    // Linked Configuration ID Handshake
    formData.append('ConfigurationId', configurationId); 

    let answerIndex = 0;

    survey.questions.forEach((q) => {
      const val = answers[q.id];
      if (!val) return;

      const qType = getQuestionType(q.type);

      if (qType === 'ImageUpload' && Array.isArray(val) && val.length > 0) {
        val.forEach((file) => {
          formData.append(`Answers[${answerIndex}].QuestionId`, q.id);
          formData.append(`Answers[${answerIndex}].FileValue`, file);
          answerIndex++;
        });
      } else if (qType === 'ImageUpload' && val instanceof File) {
        formData.append(`Answers[${answerIndex}].QuestionId`, q.id);
        formData.append(`Answers[${answerIndex}].FileValue`, val);
        answerIndex++;
      } else {
        formData.append(`Answers[${answerIndex}].QuestionId`, q.id);
        formData.append(`Answers[${answerIndex}].AnswerText`, val.toString());
        answerIndex++;
      }
    });

    try {
      const DEFAULT_UUID = '00000000-0000-0000-0000-000000000000';
      const targetToken = (token && token !== 'global') ? token : DEFAULT_UUID;
      
      const res = await fetch(`${ENDPOINTS.SURVEYS}/${targetToken}/responses`, {
        method: 'POST',
        body: formData 
      });
      if (!res.ok) {
        const errorData = await res.json();
        let errorMessage = "Submission failed.";
        if (errorData.errors) {
            errorMessage = Object.values(errorData.errors).flat().join(" ");
        } else if (errorData.message) {
            errorMessage = errorData.message;
        } else if (typeof errorData === 'string') {
            errorMessage = errorData;
        }
        throw new Error(errorMessage);
      }
      
      setCompleted(true);
    } catch (err) {
      showSnackbar(err.message, "error"); 
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <Box p={5} textAlign="center"><CircularProgress /></Box>;
  if (error) return <Box p={5}><Alert severity="error">{error}</Alert></Box>;
  if (completed) return (
    <Container maxWidth="sm" sx={{ mt: 10, textAlign: 'center' }}>
      <Paper sx={{ p: 5 }}>
        <Typography variant="h4" color="primary" gutterBottom>Thank You!</Typography>
        <Typography>Your design has been received and survey responses recorded.</Typography>
      </Paper>
    </Container>
  );

  return (
    <Container maxWidth="md" sx={{ py: 5 }}>
      <Paper elevation={1} sx={{ p: 3, mb: 4 }}>
        <Stepper activeStep={activeStep}>
          <Step><StepLabel>Your Information</StepLabel></Step>
          <Step><StepLabel>Survey Questions</StepLabel></Step>
        </Stepper>
      </Paper>

      <Paper elevation={3} sx={{ p: 4, mb: 4, borderTop: '6px solid #a8851d' }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>{survey.title}</Typography>
        <Typography variant="body1" color="text.secondary">{survey.description}</Typography>
      </Paper>

      {activeStep === 0 && (
        <Paper sx={{ p: 4, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PersonIcon sx={{ fontSize: 40, color: '#a8851d', mr: 2 }} />
            <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
              Before we start, tell us about yourself
            </Typography>
          </Box>

          <TextField
            fullWidth
            required
            label="Full Name"
            value={customerInfo.name}
            onChange={(e) => setCustomerInfo({...customerInfo, name: e.target.value})}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            required
            type="email"
            label="Email Address"
            value={customerInfo.email}
            onChange={(e) => setCustomerInfo({...customerInfo, email: e.target.value})}
            sx={{ mb: 3 }}
          />

          <TextField
            fullWidth
            label="Phone Number"
            value={customerInfo.phone}
            onChange={(e) => setCustomerInfo({...customerInfo, phone: e.target.value})}
            placeholder="+44 20 1234 5678"
            sx={{ mb: 3 }}
          />

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ bgcolor: '#a8851d', '&:hover': { bgcolor: '#8c6e18' }, px: 4 }}
            >
              Continue to Survey
            </Button>
          </Box>
        </Paper>
      )}

      {activeStep === 1 && (
        <>
          {survey.questions.map((q) => {
            const qType = getQuestionType(q.type); 

            return (
              <Paper key={q.id} sx={{ p: 4, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" gutterBottom sx={{ color: '#333' }}>
                  {q.questionText}
                </Typography>
                
                <Box sx={{ mt: 2 }}>
                  {(qType === 'Text' || qType === 'Paragraph') && (
                    <TextField 
                      fullWidth 
                      variant="outlined"
                      multiline={qType === 'Paragraph'}
                      rows={qType === 'Paragraph' ? 4 : 1}
                      placeholder="Type your answer here..."
                      value={answers[q.id] || ''} 
                      onChange={(e) => handleInputChange(q.id, e.target.value)}
                    />
                  )}

                  {qType === 'YesNo' && (
                    <RadioGroup row onChange={(e) => handleInputChange(q.id, e.target.value)}>
                      <FormControlLabel value="Yes" control={<Radio sx={{ color: '#a8851d', '&.Mui-checked': { color: '#a8851d' } }} />} label="Yes" />
                      <FormControlLabel value="No" control={<Radio sx={{ color: '#a8851d', '&.Mui-checked': { color: '#a8851d' } }} />} label="No" />
                    </RadioGroup>
                  )}

                  {qType === 'MultipleChoice' && q.options && (
                    <RadioGroup onChange={(e) => handleInputChange(q.id, e.target.value)}>
                      {JSON.parse(q.options).map(opt => (
                        <FormControlLabel key={opt} value={opt} control={<Radio />} label={opt} />
                      ))}
                    </RadioGroup>
                  )}

                  {qType === 'Checkbox' && q.options && (
                    <FormGroup>
                      {JSON.parse(q.options).map(opt => (
                        <FormControlLabel
                          key={opt}
                          label={opt}
                          control={
                            <Checkbox
                              checked={(answers[q.id] || []).includes(opt)}
                              onChange={(e) => {
                                const current = answers[q.id] || [];
                                const updated = e.target.checked
                                  ? [...current, opt]
                                  : current.filter(v => v !== opt);
                                handleInputChange(q.id, updated);
                              }}
                              sx={{ color: '#a8851d', '&.Mui-checked': { color: '#a8851d' } }}
                            />
                          }
                        />
                      ))}
                    </FormGroup>
                  )}

                  {qType === 'ImageUpload' && (
                    <Box>
                      <Button
                        component="label"
                        variant="outlined"
                        startIcon={<CloudUploadIcon />}
                        sx={{ color: '#555', borderColor: '#ccc', py: 1.5, px: 3 }}
                      >
                        {answers[q.id]?.length > 0 
                          ? `${answers[q.id].length} Photo${answers[q.id].length > 1 ? 's' : ''} Selected` 
                          : "Upload Photos (up to 5)"}
                        <input 
                          type="file" 
                          hidden 
                          multiple
                          accept="image/jpeg, image/png, image/gif, image/webp"
                          onChange={(e) => handleFileChange(q.id, e.target.files)} 
                        />
                      </Button>
                      
                      {answers[q.id] && answers[q.id].length > 0 && (
                        <Box sx={{ mt: 2 }}>
                          {answers[q.id].map((file, index) => (
                            <Chip
                              key={index}
                              label={file.name}
                              onDelete={() => removeFile(q.id, index)}
                              deleteIcon={<DeleteIcon />}
                              sx={{ m: 0.5 }}
                              color="success"
                            />
                          ))}
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </Paper>
            );
          })}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              onClick={handleBack}
              sx={{ px: 4 }}
            >
              Back
            </Button>
            <Button 
              variant="contained" 
              size="large" 
              onClick={handleSubmit}
              disabled={submitting}
              sx={{ bgcolor: '#a8851d', px: 5, py: 1.5, fontSize: '1.1rem' }}
            >
              {submitting ? 'Sending...' : 'Submit Survey'}
            </Button>
          </Box>

          <Snackbar
            open={snackbarOpen}
            autoHideDuration={4000}
            onClose={() => setSnackbarOpen(false)}
          >
            <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
              {snackbarMessage}
            </Alert>
          </Snackbar>
        </>
      )}
    </Container>
  );
};

export default PublicSurveyPage;